import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { mapValues } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { english } from "../localisations/en";
import { dateFnsLocales, LocaleName, Localisation } from "../helpers/localise";

/** This type is intentionally not exported. Don't add new format strings, use the existing ones */
type DateTimeFormat = string;

export function formatDateTime(
  date: Date,
  formatString: DateTimeFormat,
  locale: LocaleName,
  timeZone?: string | null,
): string {
  const options = { locale: dateFnsLocales[locale] ?? dateFnsLocales.en };
  try {
    if (timeZone != null) {
      return formatInTimeZone(date, timeZone, formatString, options);
    }
    return format(date, formatString, options);
  } catch {
    return "-";
  }
}

type DateTimeFormatName = keyof Localisation["dateFormats"];

export type DateTimeFormatter = (
  date: Date,
  timeZone?: string | null,
) => string;
export type DateTimeRangeFormatter = (
  start: Date,
  end: Date,
  timeZone?: string | null,
) => string;

export type DateFormatter = {
  [format in DateTimeFormatName]: DateTimeFormatter;
};

export function useDateFormatter(): DateFormatter {
  const { t, i18n } = useTranslation("dateFormats");
  return useMemo((): DateFormatter => {
    const dateTimeFormatters = mapValues(
      english.dateFormats,
      (_, format: string): DateTimeFormatter => {
        return (date, timeZone): string =>
          formatDateTime(
            date,
            t(format as DateTimeFormatName) as DateTimeFormat,
            i18n.language as LocaleName,
            timeZone,
          );
      },
    );
    return {
      ...dateTimeFormatters,
    };
  }, [t, i18n]);
}
